import { useIsWithinTailWindBreakpoint } from 'noddi-ui-web';
import { PropsWithChildren } from 'react';
import { useStationaryReservation } from '../../../stores/StationaryBookingStore';
import { BookingFlowBottomBar, BookingFlowNextButtonProps } from '../Common/BookingFlowBottomBar';
import { BookingFlowScreen, BookingFlowScreenLayoutProps } from '../Common/BookingFlowScreen';
import { NavioContactChatWidget } from '../Common/NavioContactChatWidget';
import { SelectedStationaryBookingTime } from './SelectedStationaryBookingTime';
import {
  StationaryBookingValidationModal,
  StationaryBookingValidationVariant
} from './StationaryBookingValidationModal';

type ValidationProps = {
  validationVariant?: StationaryBookingValidationVariant;
  validationBody?: Record<string, unknown>;
};

type Props = BookingFlowScreenLayoutProps & BookingFlowNextButtonProps & ValidationProps;

export const StationaryBookingScreen = ({
  children,
  onNextClicked,
  disableNextButton,
  hideNextButton,
  nextButtonLoading,
  validationVariant = 'stepIncomplete',
  validationBody,
  ...layoutProps
}: PropsWithChildren<Props>) => {
  return (
    <BookingFlowScreen
      {...layoutProps}
      defaultBottomBar={
        <StationaryFixedBottomBar
          hideBackButton={layoutProps.hideBackButton}
          onNextClicked={onNextClicked}
          disableNextButton={disableNextButton}
          hideNextButton={hideNextButton}
          nextButtonLoading={nextButtonLoading}
          validationVariant={validationVariant}
          validationBody={validationBody}
        />
      }
    >
      {children}
    </BookingFlowScreen>
  );
};

type StationaryFixedBottomBarProps = BookingFlowNextButtonProps & {
  hideBackButton?: boolean;
  validationVariant: StationaryBookingValidationVariant;
  validationBody?: Record<string, unknown>;
};

const StationaryFixedBottomBar = ({
  hideBackButton,
  validationVariant,
  validationBody,
  ...nextButtonProps
}: StationaryFixedBottomBarProps) => {
  const reservation = useStationaryReservation();
  const isWithinMd = useIsWithinTailWindBreakpoint('md');

  const selectedTime = reservation && (
    <SelectedStationaryBookingTime
      selectedTimeWindow={{ startsAt: reservation.startsAt, endsAt: reservation.endsAt }}
    />
  );

  return (
    <BookingFlowBottomBar
      {...nextButtonProps}
      hideBackButton={hideBackButton}
      // Same footer as the flow's login and summary.
      leading={<NavioContactChatWidget />}
      // On a phone the held time gets its own row: beside Back, Chat and Next it pushed Next off screen.
      summary={isWithinMd && selectedTime}
      controls={!isWithinMd && selectedTime}
      renderValidationModal={({ open, onClose }) => (
        <StationaryBookingValidationModal
          open={open}
          onClose={onClose}
          variant={validationVariant}
          body={validationBody}
        />
      )}
    />
  );
};
